/**
 * researchDataSlice — CRUD de dados de pesquisa, lentes (DataLens) e marcadores temporais (TimeMarker)
 *
 * Consumidores: DataLensModal, ResearchDataLenses, DadosPesquisa, Conexoes, Dashboard, Leituras (via driveProjectStore)
 * Dependências: googleDrive, helpers
 * Persistência: Google Drive (research-data.json por projeto)
 */
import { googleDrive } from '../../services/googleDrive';
import { ensureAuthenticated } from './helpers';
import { researchCache, getCacheKey } from './projectCache';
import { logger } from '../../utils/logger';
import { driveErrorToast } from '../../utils/driveErrorToast';
import { auditLog } from '../../utils/auditLog';
import type { DataLens, TimeMarker, LensRole, ProjectResearchData, SliceSet, SliceGet } from './types';

// Helper: persiste cachedResearchData no IndexedDB após mutações
const persistResearchCache = (get: SliceGet) => {
  const activeProjectId = get().activeProjectId;
  if (activeProjectId) {
    const cacheKey = getCacheKey('research', activeProjectId);
    researchCache.set(cacheKey, get().cachedResearchData || []);
  }
};

export const createResearchDataSlice = (set: SliceSet, get: SliceGet) => {
  // Usado pelo _backgroundRevalidate para evitar sobrescrever dado local
  // que foi alterado por uma mutação enquanto o fetch estava em andamento.
  let _lastMutationAt = 0;

  // Revalidação em background com 3 guards: isLoading, activeProjectId, _lastMutationAt.
  // Chamado pelo TTL check no load e pelo revalidateResearchDataIfStale no
  // visibilitychange (via revalidateResearchDataIfStale).
  const _backgroundRevalidate = (projectId: string, cacheKey: string) => {
    if (get().isLoadingResearchData) return;
    set({ isLoadingResearchData: true });
    const startedAt = Date.now();

    ensureAuthenticated().then(() =>
      googleDrive.getProjectResearchData(projectId)
    ).then(freshData => {
      if (get().activeProjectId !== projectId) return;
      if (_lastMutationAt > startedAt) {
        set({ isLoadingResearchData: false });
        logger.info('researchDataSlice', 'Revalidação descartada — mutação local mais recente');
        return;
      }
      if (freshData !== null) {
        set({ cachedResearchData: freshData, researchDataLoadedAt: Date.now(), isLoadingResearchData: false });
        researchCache.set(cacheKey, freshData);
      } else {
        set({ isLoadingResearchData: false });
      }
    }).catch(err => {
      if (get().activeProjectId === projectId) {
        set({ isLoadingResearchData: false });
      }
      logger.error('[researchDataSlice] Revalidação em background falhou:', String(err));
    });
  };

  return {
  // === FUNÇÕES DE DADOS DE PESQUISA ===

  // Carrega dados de pesquisa do projeto ativo com stale-while-revalidate
  loadActiveProjectResearchData: async () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      set({ cachedResearchData: [], researchDataLoaded: true });
      return [];
    }

    // TTL check: se carregou há menos de 5 min, serve do Zustand sem revalidar
    const loadedAt = get().researchDataLoadedAt;
    if (get().researchDataLoaded && loadedAt && Date.now() - loadedAt < 300_000) {
      return get().cachedResearchData || [];
    }

    // Se carregou mas TTL expirou, serve stale + revalida em background
    if (get().researchDataLoaded && get().cachedResearchData) {
      const cacheKey = getCacheKey('research', activeProjectId);
      _backgroundRevalidate(activeProjectId, cacheKey);
      return get().cachedResearchData;
    }

    // Se já está carregando, não inicia nova chamada
    if (get().isLoadingResearchData) {
      return get().cachedResearchData || [];
    }

    const cacheKey = getCacheKey('research', activeProjectId);

    // 1. Tentar IndexedDB (instantâneo)
    try {
      const cached = await researchCache.get(cacheKey);
      if (cached.success && cached.data !== null) {
        // Serve stale imediatamente
        set({ cachedResearchData: cached.data, researchDataLoaded: true, researchDataLoadedAt: Date.now() });

        // 2. Revalidar em background (com 3 guards)
        _backgroundRevalidate(activeProjectId, cacheKey);

        return cached.data;
      }
    } catch {
      // IndexedDB indisponível
    }

    // 3. Sem cache: buscar do Drive
    set({ isLoadingResearchData: true });
    const firstLoadStartedAt = Date.now();

    try {
      await ensureAuthenticated();
      const data = await googleDrive.getProjectResearchData(activeProjectId);

      // Guard: mutação local ocorreu durante o fetch — dados locais são mais recentes
      if (_lastMutationAt > firstLoadStartedAt) {
        set({ isLoadingResearchData: false });
        return get().cachedResearchData || [];
      }

      const result = data || [];
      set({ cachedResearchData: result, researchDataLoaded: true, researchDataLoadedAt: Date.now(), isLoadingResearchData: false });

      // Salvar no IndexedDB
      researchCache.set(cacheKey, result);

      return data;
    } catch (error) {
      set({ isLoadingResearchData: false });
      throw error;
    }
  },

  // Retorna dados de pesquisa do cache (síncrono)
  getCachedResearchData: () => {
    return get().cachedResearchData || [];
  },

  // Invalida o cache (força recarregar na próxima vez)
  invalidateResearchDataCache: () => {
    const activeProjectId = get().activeProjectId;
    set({ cachedResearchData: [], researchDataLoaded: false, researchDataLoadedAt: null });
    if (activeProjectId) {
      researchCache.delete(getCacheKey('research', activeProjectId));
    }
  },

  // Adiciona dado de pesquisa ao projeto ativo
  addResearchDataToActiveProject: async (repositoryItemId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    try {
      await ensureAuthenticated();
      const newDataId = await googleDrive.addResearchDataToProject(activeProjectId, repositoryItemId);

      // Drive confirmou — atualiza cache localmente
      const newResearchData: ProjectResearchData = {
        id: newDataId,
        repositoryItemId,
        addedAt: new Date().toISOString(),
        relevance: null,
        integrationTags: [],
        notes: '',
        notesCount: 0,
        isFavorite: false,
        groupId: null,
        lenses: [],
        markers: [],
      };
      set(state => ({
        cachedResearchData: [...(state.cachedResearchData || []), newResearchData]
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });

      // Persiste no IndexedDB
      persistResearchCache(get);

      auditLog({ operation: 'create', target: 'dado-pesquisa', targetId: newDataId, detail: `Adicionado ao projeto ${activeProjectId}`, success: true });
      return newDataId;
    } catch (error) {
      logger.error('[researchDataSlice.addResearchData]', error);
      driveErrorToast(error, 'Não foi possível adicionar o dado de pesquisa. Tente novamente.');
      auditLog({ operation: 'create', target: 'dado-pesquisa', detail: `Falha ao adicionar ao projeto ${activeProjectId}`, success: false, error: String(error) });
      return '';
    }
  },

  // Remove dado de pesquisa do projeto ativo
  removeResearchDataFromActiveProject: async (dataId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    try {
      await ensureAuthenticated();
      await googleDrive.removeResearchDataFromProject(activeProjectId, dataId);

      // Drive confirmou — atualiza cache localmente
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).filter(d => d.id !== dataId)
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });

      // Persiste no IndexedDB
      persistResearchCache(get);
      auditLog({ operation: 'delete', target: 'dado-pesquisa', targetId: dataId, detail: `Removido do projeto ${activeProjectId}`, success: true });
    } catch (error) {
      logger.error('[researchDataSlice.removeResearchData]', error);
      driveErrorToast(error, 'Não foi possível remover o dado de pesquisa. Tente novamente.');
      auditLog({ operation: 'delete', target: 'dado-pesquisa', targetId: dataId, detail: `Falha ao remover do projeto ${activeProjectId}`, success: false, error: String(error) });
    }
  },

  // Atualiza dado de pesquisa no projeto ativo
  updateResearchDataInActiveProject: async (dataId: string, updates: {
    relevance?: 'central' | 'important' | 'doubt' | null;
    integrationTags?: string[];
    notes?: string;
    notesCount?: number;
    isFavorite?: boolean;
    status?: 'pending' | 'analyzing' | 'analyzed' | null;
    groupId?: string | null;
    lenses?: DataLens[]; // NOVO: Fase 6
    imageAnnotations?: string;  // v4.0: Anotações de imagem
    imageRotation?: number;     // v4.0: Rotação da imagem
  }) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    try {
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, updates as Partial<ProjectResearchData>);

      // Drive confirmou — atualiza cache localmente
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, ...updates } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });

      // Persiste no IndexedDB
      persistResearchCache(get);
      auditLog({ operation: 'update', target: 'dado-pesquisa', targetId: dataId, detail: `Atualizado no projeto ${activeProjectId}: ${Object.keys(updates).join(', ')}`, success: true });
    } catch (error) {
      logger.error('[researchDataSlice.updateResearchData]', error);
      driveErrorToast(error, 'Não foi possível atualizar o dado de pesquisa. Tente novamente.');
      auditLog({ operation: 'update', target: 'dado-pesquisa', targetId: dataId, detail: `Falha ao atualizar no projeto ${activeProjectId}`, success: false, error: String(error) });
    }
  },

  // ============================================
  // FUNÇÕES DE LENTES DE PESQUISA (FASE 6)
  // ============================================

  // Adiciona lente a um dado de pesquisa
  addLensToData: async (dataId: string, referenceId: string, role: LensRole, note?: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    // Verificar se já existe lente com essa referência
    const existingLenses = currentData.lenses || [];
    if (existingLenses.some((l: DataLens) => l.referenceId === referenceId)) {
      throw new Error('Esta referência já está conectada a este dado');
    }

    // Criar nova lente
    const newLens: DataLens = {
      id: `lens-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      referenceId,
      role,
      note,
      createdAt: new Date().toISOString()
    };

    const updatedLenses = [...existingLenses, newLens];

    try {
      // Salvar no Drive
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { lenses: updatedLenses } as Partial<ProjectResearchData>);

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, lenses: updatedLenses } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });
      persistResearchCache(get);

      auditLog({ operation: 'create', target: 'lente', targetId: newLens.id, detail: `Lente ${role} adicionada ao dado ${dataId}`, success: true });
      return newLens.id;
    } catch (error) {
      logger.error('[researchDataSlice.addLensToData]', error);
      driveErrorToast(error, 'Não foi possível adicionar a lente. Tente novamente.');
      auditLog({ operation: 'create', target: 'lente', detail: `Falha ao adicionar lente ao dado ${dataId}`, success: false, error: String(error) });
      return '';
    }
  },

  // Atualiza lente existente
  updateLensInData: async (dataId: string, lensId: string, updates: { role?: LensRole; note?: string }) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    const existingLenses = currentData.lenses || [];
    const updatedLenses = existingLenses.map((l: DataLens) =>
      l.id === lensId ? { ...l, ...updates, updatedAt: new Date().toISOString() } : l
    );

    try {
      // Salvar no Drive
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { lenses: updatedLenses } as Partial<ProjectResearchData>);

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, lenses: updatedLenses } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });
      persistResearchCache(get);
      auditLog({ operation: 'update', target: 'lente', targetId: lensId, detail: `Lente atualizada no dado ${dataId}: ${Object.keys(updates).join(', ')}`, success: true });
    } catch (error) {
      logger.error('[researchDataSlice.updateLensInData]', error);
      driveErrorToast(error, 'Não foi possível atualizar a lente. Tente novamente.');
      auditLog({ operation: 'update', target: 'lente', targetId: lensId, detail: `Falha ao atualizar lente no dado ${dataId}`, success: false, error: String(error) });
    }
  },

  // Remove lente de um dado
  removeLensFromData: async (dataId: string, lensId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    const existingLenses = currentData.lenses || [];
    const updatedLenses = existingLenses.filter((l: DataLens) => l.id !== lensId);

    try {
      // Salvar no Drive
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { lenses: updatedLenses } as Partial<ProjectResearchData>);

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, lenses: updatedLenses } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });
      persistResearchCache(get);
      auditLog({ operation: 'delete', target: 'lente', targetId: lensId, detail: `Lente removida do dado ${dataId}`, success: true });
    } catch (error) {
      logger.error('[researchDataSlice.removeLensFromData]', error);
      driveErrorToast(error, 'Não foi possível remover a lente. Tente novamente.');
      auditLog({ operation: 'delete', target: 'lente', targetId: lensId, detail: `Falha ao remover lente do dado ${dataId}`, success: false, error: String(error) });
    }
  },

  // Retorna lentes de um dado específico (síncrono)
  getDataLenses: (dataId: string) => {
    const data = (get().cachedResearchData || []).find(d => d.id === dataId);
    return data?.lenses || [];
  },

  // ============================================
  // FUNÇÕES DE MARCADORES TEMPORAIS (FASE 5)
  // ============================================

  // Adiciona marcador temporal a um dado de pesquisa
  addTimeMarker: async (dataId: string, marker: Omit<TimeMarker, 'id' | 'createdAt'>) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    // Criar novo marcador
    const existingMarkers = currentData.markers || [];
    const newMarker: TimeMarker = {
      id: `marker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      ...marker,
      createdAt: new Date().toISOString()
    };

    const updatedMarkers = [...existingMarkers, newMarker];

    try {
      // Salvar no Drive
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { markers: updatedMarkers } as Partial<ProjectResearchData>);

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, markers: updatedMarkers } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });
      persistResearchCache(get);

      auditLog({ operation: 'create', target: 'marcador-temporal', targetId: newMarker.id, detail: `Marcador adicionado ao dado ${dataId}`, success: true });
      return newMarker.id;
    } catch (error) {
      logger.error('[researchDataSlice.addTimeMarker]', error);
      driveErrorToast(error, 'Não foi possível adicionar o marcador. Tente novamente.');
      auditLog({ operation: 'create', target: 'marcador-temporal', detail: `Falha ao adicionar marcador ao dado ${dataId}`, success: false, error: String(error) });
      return '';
    }
  },

  // Atualiza marcador existente
  updateTimeMarker: async (dataId: string, markerId: string, updates: Partial<TimeMarker>) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    const existingMarkers = currentData.markers || [];
    const updatedMarkers = existingMarkers.map((m: TimeMarker) =>
      m.id === markerId ? { ...m, ...updates, updatedAt: new Date().toISOString() } : m
    );

    try {
      // Salvar no Drive
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { markers: updatedMarkers } as Partial<ProjectResearchData>);

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, markers: updatedMarkers } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });
      persistResearchCache(get);
      auditLog({ operation: 'update', target: 'marcador-temporal', targetId: markerId, detail: `Marcador atualizado no dado ${dataId}`, success: true });
    } catch (error) {
      logger.error('[researchDataSlice.updateTimeMarker]', error);
      driveErrorToast(error, 'Não foi possível atualizar o marcador. Tente novamente.');
      auditLog({ operation: 'update', target: 'marcador-temporal', targetId: markerId, detail: `Falha ao atualizar marcador no dado ${dataId}`, success: false, error: String(error) });
    }
  },

  // Remove marcador de um dado
  removeTimeMarker: async (dataId: string, markerId: string) => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) {
      throw new Error('Nenhum projeto ativo selecionado');
    }

    // Buscar dado atual
    const currentData = (get().cachedResearchData || []).find(d => d.id === dataId);
    if (!currentData) {
      throw new Error('Dado não encontrado');
    }

    const existingMarkers = currentData.markers || [];
    const updatedMarkers = existingMarkers.filter((m: TimeMarker) => m.id !== markerId);

    try {
      // Salvar no Drive
      await ensureAuthenticated();
      await googleDrive.updateProjectResearchData(activeProjectId, dataId, { markers: updatedMarkers } as Partial<ProjectResearchData>);

      // Drive confirmou — atualizar cache local
      set(state => ({
        cachedResearchData: (state.cachedResearchData || []).map(d =>
          d.id === dataId ? { ...d, markers: updatedMarkers } : d
        )
      }));
      _lastMutationAt = Date.now();
      set({ researchDataLoadedAt: Date.now() });
      persistResearchCache(get);
      auditLog({ operation: 'delete', target: 'marcador-temporal', targetId: markerId, detail: `Marcador removido do dado ${dataId}`, success: true });
    } catch (error) {
      logger.error('[researchDataSlice.removeTimeMarker]', error);
      driveErrorToast(error, 'Não foi possível remover o marcador. Tente novamente.');
      auditLog({ operation: 'delete', target: 'marcador-temporal', targetId: markerId, detail: `Falha ao remover marcador no dado ${dataId}`, success: false, error: String(error) });
    }
  },

  // Retorna marcadores de um dado específico (síncrono)
  getTimeMarkers: (dataId: string) => {
    const data = (get().cachedResearchData || []).find(d => d.id === dataId);
    return data?.markers || [];
  },

  // Revalida dados de pesquisa se TTL expirou (entry point para visibilitychange)
  revalidateResearchDataIfStale: () => {
    const activeProjectId = get().activeProjectId;
    if (!activeProjectId) return;
    if (!get().researchDataLoaded) return;
    const loadedAt = get().researchDataLoadedAt;
    if (loadedAt && Date.now() - loadedAt < 300_000) return;
    const cacheKey = getCacheKey('research', activeProjectId);
    _backgroundRevalidate(activeProjectId, cacheKey);
  },
  };
};
